import React, { useState, useEffect } from 'react'
import { Modal, Select, Upload } from 'antd'
import Cancel from 'assets/Images/close.svg'
import VectorImg from 'assets/Images/Vector.svg'
import { successMessage, failureMessage } from '../../../api/global'
import { read, utils } from 'xlsx'
import CsvImg from 'assets/Images/pdf 1.svg'
import CheckImg from 'assets/Images/checked.svg'
import { useMutation } from '@tanstack/react-query'
import warningImg from 'assets/Images/ImportWarning.svg'
import plusImg from 'assets/Images/ImportPlus.svg'
import Loader from 'helper/Loader'
import { importBlogFile, confirmBlogImport } from 'api/Blogs/Blogs'
import '../../Activity/Headers/activityImportStyle.scss'

const { Option } = Select

const attributeOptions = [
  { key: 'title', display: 'Blog Title', required: true },
  { key: 'content', display: 'Blog Content', required: true },
  { key: 'organizationId', display: 'Organization Id', required: true },
  { key: 'authorIds', display: 'Author Ids (comma separated)', required: true },
  { key: 'readingTime', display: 'Reading Time (minutes)', required: false },
  { key: 'excerpt', display: 'Excerpt', required: false },
  { key: 'sharingOptions', display: 'Sharing Options (comma separated)', required: false },
  { key: 'visibility', display: 'Visibility', required: false },
  { key: 'featureImageId', display: 'Feature Image Id', required: false },
]

const ImportBlog = ({ visible, onClose, onSuccess }: { visible: boolean; onClose: () => void; onSuccess?: () => void }) => {
  const [fileList, setFileList] = useState([])
  const [fileHeaders, setFileHeaders] = useState([])
  const [fileContent, setFileContent] = useState('')
  const [isImportButtonDisabled, setIsImportButtonDisabled] = useState(true)
  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false)
  const [attributeMapping, setAttributeMapping] = useState<Record<string, string>>({})
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(true)
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false)
  const [isResultModalVisible, setIsResultModalVisible] = useState(false)
  const [apiResponse, setApiResponse] = useState<any>(null)
  const [isLogsModalVisible, setIsLogsModalVisible] = useState(false)

  const handleFileUpload = ({ file }: any) => {
    const reader = new FileReader()
    reader.onload = (e: any) => {
      const binaryStr = e.target.result
      const workbook = read(binaryStr, { type: 'binary', cellText: false, cellDates: true })
      const sheetName = workbook.SheetNames[0]
      const sheet = workbook.Sheets[sheetName]
      const csvData = utils.sheet_to_csv(sheet, { rawNumbers: true })
      const headers = csvData.split('\n')[0].split(',')
      setFileHeaders(headers)
      setFileContent(csvData)
      setIsImportButtonDisabled(false)
      successMessage(`${file.name} uploaded successfully`)
    }
    reader.onerror = () => failureMessage(`${file.name} upload failed.`)

    if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      reader.readAsBinaryString(file)
    } else {
      reader.readAsText(file)
    }
  }

  const uploadProps = {
    beforeUpload: (file: any) => {
      handleFileUpload({ file })
      return false
    },
  }

  useEffect(() => {
    const allRequiredMapped = attributeOptions
      .filter(attr => attr.required)
      .every(attr => attributeMapping[attr.key] && attributeMapping[attr.key] !== 'Do not import')
    setIsContinueButtonDisabled(!allRequiredMapped)
  }, [attributeMapping])

  const resetMapping = () => {
    const newMapping: Record<string, string> = {}
    attributeOptions.forEach(attr => { newMapping[attr.key] = 'Do not import' })
    setAttributeMapping(newMapping)
  }

  const handleBack = () => {
    resetMapping()
    setIsMappingModalVisible(false)
  }

  const handleAttributeChange = (attribute: string, value: string) => {
    setAttributeMapping({ ...attributeMapping, [attribute]: value })
  }

  const importMutation = useMutation({
    mutationFn: async ({ payload }: any) => importBlogFile(payload),
    onSuccess: (data: any) => {
      if (data) {
        setIsReviewModalVisible(false)
        setApiResponse(data)
        setIsResultModalVisible(true)
      }
    },
  })

  const handleImport = () => {
    const lines = fileContent.split('\n')
    const headers = lines[0].split(',')
    const mappedData: any[] = []

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue
      const values = lines[i].split(',')
      const mappedRow: Record<string, string> = {}
      let hasValidData = false

      attributeOptions.forEach(attr => {
        const csvColumn = attributeMapping[attr.key]
        if (csvColumn && csvColumn !== 'Do not import') {
          const columnIndex = headers.indexOf(csvColumn)
          if (columnIndex !== -1) {
            const value = values[columnIndex]?.trim().replace(/(^"|"$)/g, '') || ''
            mappedRow[attr.key] = value
            if (value) hasValidData = true
          }
        }
      })

      if (hasValidData) mappedData.push(mappedRow)
    }

    importMutation.mutate({ payload: mappedData })
  }

  const confirmMutation = useMutation({
    mutationFn: async ({ payload }: any) => confirmBlogImport(payload),
    onSuccess: (data: any) => {
      if (data) {
        successMessage(data)
        setIsResultModalVisible(false)
        onClose()
        onSuccess?.()
      }
    },
  })

  const handleComplete = () => {
    confirmMutation.mutate({ payload: { validationId: apiResponse?.validationId, confirm: true } })
  }

  const handleCancelImport = () => {
    confirmMutation.mutate({ payload: { validationId: apiResponse?.validationId, confirm: false } })
    setIsResultModalVisible(false)
    onClose()
  }

  const downloadErrorLogs = () => {
    const logs = apiResponse?.logs || []
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [['Row', 'Message'], ...logs.map((l: any) => [l.row + 1, l.msg])].map(e => e.join(',')).join('\n')
    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', 'blog-import-errors.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <Modal visible={visible} footer={null} onCancel={onClose} className="import-modal">
        <div className="popup-header">
          <h2 className="importText">Import Blogs</h2>
          <img src={Cancel} alt="close" onClick={onClose} />
        </div>
        <Upload.Dragger {...uploadProps} accept=".csv,.xlsx" fileList={fileList} onChange={info => setFileList(info.fileList)}>
          <p className="ant-upload-drag-icon">
            <img alt="upload" src={VectorImg} />
          </p>
          <p className="dragText">Drag and drop document here or <b className="uploadText">upload document</b></p>
          <p className="ant-upload-hint" style={{ marginTop: '10px' }}>CSV or XLSX formats</p>
        </Upload.Dragger>
        <div className="flex justify-end w-full mt-20">
          <button className="prim-sm-btn" style={{ background: '#EBEDF1', color: 'black' }} onClick={onClose}>Cancel</button>
          <button className="prim-sm-btn" disabled={isImportButtonDisabled} onClick={() => setIsMappingModalVisible(true)}>Import</button>
        </div>
      </Modal>

      <Modal visible={isMappingModalVisible} footer={null} onCancel={handleBack} className="mapping-modal">
        <div>
          <div className="popup-header-2">
            <h2>Customize your attributes</h2>
            <img src={Cancel} alt="close" onClick={handleBack} />
          </div>
          <p className="attributeText">Select the correct attribute for each column you'd like to import.</p>
          <p className="schema-Text">Schema mapping</p>
          <div className="schema-mapping-header">
            <span className="attribute-header">Attribute</span>
            <span className="csv-column-header">CSV column</span>
          </div>
          <div className="schema-mapping h-25rem overflow-auto">
            {attributeOptions.map(attr => (
              <div key={attr.key} className="mapping-row">
                <div className="fleximg">
                  <img alt="csv" src={CsvImg} style={{ marginRight: '5px' }} />
                  <span className={`attribute-label position-relative ${attr.required ? 'required-input' : ''}`}>{attr.display}</span>
                </div>
                <Select value={attributeMapping[attr.key] || 'Do not import'} className="csv-column-select" onChange={value => handleAttributeChange(attr.key, value)}>
                  <Option value="Do not import">Do not import</Option>
                  {fileHeaders.map((header: string) => <Option key={header} value={header}>{header}</Option>)}
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-end w-full mt-20">
            <button className="prim-sm-btn" style={{ background: '#EBEDF1', color: 'black' }} onClick={handleBack}>Back</button>
            <button className="prim-sm-btn" disabled={isContinueButtonDisabled} onClick={() => { setIsMappingModalVisible(false); setIsReviewModalVisible(true) }}>Continue</button>
          </div>
        </div>
      </Modal>

      <Modal visible={isReviewModalVisible} footer={null} onCancel={() => setIsReviewModalVisible(false)} className="review-modal">
        <div>
          <div className="popup-header-2">
            <h2>Review values</h2>
            <img src={Cancel} alt="close" onClick={() => { setIsReviewModalVisible(false); setIsMappingModalVisible(true) }} />
          </div>
          <p className="attributeText">Check that the values are correct before importing.</p>
          <p className="schema-Text">Schema mapping</p>
          <div className="schema-mapping-header">
            <span className="attribute-header">Attribute</span>
            <span className="csv-column-header">CSV column</span>
          </div>
          <div className="schema-mapping h-25rem overflow-auto">
            {Object.keys(attributeMapping).map(key =>
              attributeMapping[key] !== 'Do not import' && (
                <div key={key} className="mapping-row">
                  <div className="fleximg-2">
                    <img alt="check" src={CheckImg} style={{ marginRight: '10px' }} />
                    <img alt="csv" src={CsvImg} style={{ marginRight: '8px' }} />
                    <span className="attribute-label position-relative">{attributeOptions.find(a => a.key === key)?.display}</span>
                  </div>
                  <span className="csv-column-label">{attributeMapping[key].replace(/"/g, '')}</span>
                </div>
              )
            )}
          </div>
          <div className="flex justify-end w-full mt-20">
            <button className="prim-sm-btn" style={{ background: '#EBEDF1', color: 'black', marginRight: '10px' }} onClick={() => { setIsReviewModalVisible(false); setIsMappingModalVisible(true) }}>Back</button>
            <button className="prim-sm-btn" onClick={handleImport}>{importMutation.isLoading ? <Loader /> : 'Continue'}</button>
          </div>
        </div>
      </Modal>

      <Modal visible={isResultModalVisible} footer={null} onCancel={() => setIsResultModalVisible(false)} className="completeModal">
        <div className="popup-header-2">
          <h2>Complete import</h2>
          <img src={Cancel} alt="close" onClick={() => setIsResultModalVisible(false)} />
        </div>
        <div className="d-flex mt-20">
          <div className="recordFill">
            <div className="flex">
              <img src={warningImg} alt="warning" />
              <span className="failedText">{apiResponse?.stats?.invalid}</span>
            </div>
            <span className="text">Records failed</span>
          </div>
          <div className="recordFill" style={{ marginLeft: '10px' }}>
            <div className="flex">
              <img src={plusImg} alt="plus" />
              <span className="SuccessText">{apiResponse?.stats?.valid}</span>
            </div>
            <span className="text">Records will be added</span>
          </div>
        </div>
        <div className="flex justify-end w-full mt-20">
          {apiResponse?.stats?.invalid > 0 && (
            <>
              <button className="prim-sm-btn" style={{ background: 'transparent', border: '1px solid #2BA7CA', color: '#2BA7CA', marginRight: '10px' }} onClick={handleCancelImport}>Cancel</button>
              <button className="prim-sm-btn" style={{ background: 'transparent', border: '1px solid #2BA7CA', color: '#2BA7CA', marginRight: '10px' }} onClick={() => setIsLogsModalVisible(true)}>View logs</button>
            </>
          )}
          <button className="prim-sm-btn" onClick={handleComplete}>{confirmMutation.isLoading ? <Loader /> : 'Complete'}</button>
        </div>
      </Modal>

      <Modal visible={isLogsModalVisible} footer={null} className="logsModal">
        <div className="popup-header-2">
          <h2>Logs</h2>
          <img src={Cancel} alt="close" onClick={() => setIsLogsModalVisible(false)} />
        </div>
        <div className="schema-mapping h-25rem overflow-auto">
          <table>
            <thead>
              <tr>
                <th colSpan={1}></th>
                <th>Row</th>
                <th colSpan={10}></th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {apiResponse?.logs.map((log: any, index: number) => (
                <tr key={index}>
                  <td colSpan={1}></td>
                  <td style={{ textAlign: 'center' }} className="errorMessage">{log.row + 1}</td>
                  <td colSpan={10}></td>
                  <td className="errorMessage">{log.msg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end w-full mt-20">
          <button className="prim-sm-btn" style={{ background: 'transparent', border: '1px solid #2BA7CA', color: '#2BA7CA', marginRight: '10px' }} onClick={() => setIsLogsModalVisible(false)}>Back</button>
          <button className="prim-sm-btn" onClick={downloadErrorLogs}>Download</button>
        </div>
      </Modal>
    </>
  )
}

export default ImportBlog
